import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { getRequirementsGathering, saveRequirementsGathering, fetchRequirementsGatheringFromSOW } from '../../utils/discoveryPhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const RequirementsGatheringPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [requirements, setRequirements] = useState({
    overview: '',
    requirements: {
      functional: [],
      nonFunctional: [],
      business: [],
      technical: []
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingRequirements, setEditingRequirements] = useState({
    overview: '',
    requirements: {
      functional: [],
      nonFunctional: [],
      business: [],
      technical: []
    }
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({});
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load requirements gathering data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadRequirementsGatheringFromDB();
    }
  }, [projectId]);
  
  // Load requirements gathering from database
  const loadRequirementsGatheringFromDB = async () => {
    setLoading(true);
    try {
      const requirementsData = await getRequirementsGathering(projectId);
      if (requirementsData) {
        setRequirements(requirementsData);
        setEditingRequirements(requirementsData);
      }
    } catch (error) {
      console.error('Error loading requirements gathering:', error);
      showSnackbar('Error loading requirements gathering data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Save requirements gathering to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveRequirementsGathering(projectId, editingRequirements);
      setRequirements(editingRequirements);
      setEditMode(false);
      showSnackbar('Requirements gathering saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving requirements gathering:', error);
      showSnackbar('Error saving requirements gathering', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch requirements gathering from project documents using AI
  const handleFetchFromSOW = async () => {
    setAiLoading(true);
    try {
      const response = await fetchRequirementsGatheringFromSOW(projectId);
      if (response.data) {
        setEditingRequirements(response.data);
        setRequirements(response.data);
        showSnackbar('Requirements gathering generated from project documents successfully!', 'success');
      }
    } catch (error) {
      console.error('Error fetching requirements gathering from project documents:', error);
      showSnackbar('Error generating requirements gathering from project documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Dialog functions
  const handleOpenDialog = (type, item = null, index = null) => {
    setDialogType(type);
    
    if (type === 'functional') {
      setDialogItem(item || { id: '', title: '', description: '', priority: 'Medium', category: '', acceptanceCriteria: [] });
    } else if (type === 'nonFunctional') {
      setDialogItem(item || { id: '', title: '', description: '', priority: 'Medium', category: 'Performance', constraint: '' });
    } else if (type === 'business') {
      setDialogItem(item || { id: '', title: '', description: '', priority: 'Medium', businessValue: '', stakeholder: '' });
    } else if (type === 'technical') {
      setDialogItem(item || { id: '', title: '', description: '', priority: 'Medium', technology: '', constraints: [] });
    }
    
    setDialogItemIndex(index);
    setOpenDialog(true);
  };
  
  const handleSaveDialog = () => {
    const newData = { ...editingRequirements };
    
    if (dialogItemIndex !== null) {
      // Edit existing item
      newData.requirements[dialogType][dialogItemIndex] = dialogItem;
    } else {
      // Add new item
      newData.requirements[dialogType].push(dialogItem);
    }
    
    setEditingRequirements(newData);
    setOpenDialog(false);
  };
  
  const handleDeleteItem = (type, index) => {
    const newData = { ...editingRequirements };
    newData.requirements[type].splice(index, 1);
    setEditingRequirements(newData);
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const renderRequirementCard = (requirement, index, type) => {
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {requirement.title}
            </Typography>
            <Chip 
              label={requirement.priority} 
              color={getPriorityColor(requirement.priority)}
              size="small"
            />
          </Box>
          
          {requirement.id && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>ID:</strong> {requirement.id}
            </Typography>
          )}
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {requirement.description}
          </Typography>
          
          {/* Type-specific fields */}
          {type === 'functional' && (
            <>
              {requirement.category && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Category:</strong> {requirement.category}
                </Typography>
              )}
              {requirement.acceptanceCriteria && requirement.acceptanceCriteria.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Acceptance Criteria:</strong>
                  </Typography>
                  <List dense>
                    {requirement.acceptanceCriteria.map((criteria, idx) => (
                      <ListItem key={idx} sx={{ pl: 2 }}>
                        <ListItemText primary={`• ${criteria}`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
          
          {type === 'nonFunctional' && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Category:</strong> {requirement.category}
              </Typography>
              {requirement.constraint && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Constraint:</strong> {requirement.constraint}
                </Typography>
              )}
            </>
          )}
          
          {type === 'business' && (
            <>
              {requirement.businessValue && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Business Value:</strong> {requirement.businessValue}
                </Typography>
              )}
              {requirement.stakeholder && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Stakeholder:</strong> {requirement.stakeholder}
                </Typography>
              )}
            </>
          )}
          
          {type === 'technical' && (
            <>
              {requirement.technology && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Technology:</strong> {requirement.technology}
                </Typography>
              )}
              {requirement.constraints && requirement.constraints.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Constraints:</strong>
                  </Typography>
                  <List dense>
                    {requirement.constraints.map((constraint, idx) => (
                      <ListItem key={idx} sx={{ pl: 2 }}>
                        <ListItemText primary={`• ${constraint}`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog(type, requirement, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem(type, index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderRequirementsSection = (type, title, requirements) => {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{title}</Typography>
          <Chip 
            label={requirements.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog(type)}
                sx={{ mb: 2 }}
              >
                Add {title.slice(0, -1)}
              </Button>
            )}
            {requirements.map((requirement, index) => 
              renderRequirementCard(requirement, index, type)
            )}
            {requirements.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No {title.toLowerCase()} defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  if (loading && !requirements.overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <AssignmentIcon />
            Requirements Gathering
          </SectionTitle>
          <Box>
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleFetchFromSOW}
                  disabled={aiLoading}
                  sx={{ mr: 1, backgroundColor: '#F5821F' }}
                >
                  {aiLoading ? <CircularProgress size={20} /> : 'Fetch From Project Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    setEditingRequirements(requirements);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Gather and document functional and non-functional requirements for {project?.name}.
        </Typography>
      </Paper>
      
      {/* Overview Section */}
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Requirements Gathering Overview"
              value={editingRequirements.overview}
              onChange={(e) => setEditingRequirements({
                ...editingRequirements,
                overview: e.target.value
              })}
            />
          ) : (
            <Typography>
              {requirements.overview || 'No overview available. Click "Edit" to add content or "Fetch From Project Documents" to generate from your project documents.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Functional Requirements */}
      {renderRequirementsSection('functional', 'Functional Requirements', editingRequirements.requirements.functional)}
      
      {/* Non-Functional Requirements */}
      {renderRequirementsSection('nonFunctional', 'Non-Functional Requirements', editingRequirements.requirements.nonFunctional)}
      
      {/* Business Requirements */}
      {renderRequirementsSection('business', 'Business Requirements', editingRequirements.requirements.business)}
      
      {/* Technical Requirements */}
      {renderRequirementsSection('technical', 'Technical Requirements', editingRequirements.requirements.technical)}
      
      {/* Dialog for adding/editing requirements */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex !== null ? 'Edit' : 'Add'} {dialogType} Requirement
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Common fields for all requirement types */}
            <TextField
              fullWidth
              label="ID"
              value={dialogItem.id || ''}
              onChange={(e) => setDialogItem({ ...dialogItem, id: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Title"
              value={dialogItem.title || ''}
              onChange={(e) => setDialogItem({ ...dialogItem, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={dialogItem.description || ''}
              onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              label="Priority"
              value={dialogItem.priority || 'Medium'}
              onChange={(e) => setDialogItem({ ...dialogItem, priority: e.target.value })}
              SelectProps={{ native: true }}
              sx={{ mb: 2 }}
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </TextField>
            
            {/* Type-specific fields */}
            {dialogType === 'functional' && (
              <TextField
                fullWidth
                label="Category"
                value={dialogItem.category || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                sx={{ mb: 2 }}
              />
            )}
            
            {dialogType === 'nonFunctional' && (
              <>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={dialogItem.category || 'Performance'}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="Performance">Performance</option>
                  <option value="Security">Security</option>
                  <option value="Usability">Usability</option>
                  <option value="Reliability">Reliability</option>
                  <option value="Scalability">Scalability</option>
                  <option value="Maintainability">Maintainability</option>
                  <option value="Compatibility">Compatibility</option>
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Constraint"
                  value={dialogItem.constraint || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, constraint: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'business' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Business Value"
                  value={dialogItem.businessValue || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, businessValue: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Stakeholder"
                  value={dialogItem.stakeholder || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, stakeholder: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'technical' && (
              <TextField
                fullWidth
                label="Technology"
                value={dialogItem.technology || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, technology: e.target.value })}
                sx={{ mb: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            {dialogItemIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequirementsGatheringPage;